import { create } from 'zustand';
import { CartItem } from '../types';

const CART_STORAGE_KEY = 'mindalitix_cart';

/**
 * The cart as localStorage last left it, or an empty one.
 *
 * `JSON.parse` was called bare. This runs during `create()`, at module
 * evaluation — so a `mindalitix_cart` key holding anything unparseable threw
 * before React ever mounted and took the entire application down with it, not
 * just the cart. A stored value that parses to something other than an array is
 * just as fatal one step later, where `items.find` is called on it.
 *
 * A basket is not worth a white screen: an unreadable one is discarded.
 */
const getInitialCartState = (): CartItem[] => {
  if (typeof window === 'undefined') return [];

  try {
    const storedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (!storedCart) return [];
    const parsed = JSON.parse(storedCart);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    localStorage.removeItem(CART_STORAGE_KEY);
    return [];
  }
};

interface CartState {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (courseId: string) => void;
  updateQuantity: (courseId: string, quantity: number) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
}

export const useCartStore = create<CartState>((set, get) => ({
  items: getInitialCartState(),
  addItem: (item) => {
    set((state) => {
      const existingItem = state.items.find((i) => i.courseId === item.courseId);
      let updatedItems;
      if (existingItem) {
        updatedItems = state.items.map((i) =>
          i.courseId === item.courseId ? { ...i, quantity: i.quantity + item.quantity } : i
        );
      } else {
        updatedItems = [...state.items, item];
      }
      if (typeof window !== 'undefined') {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(updatedItems));
      }
      return { items: updatedItems };
    });
  },
  removeItem: (courseId) => {
    set((state) => {
      const updatedItems = state.items.filter((item) => item.courseId !== courseId);
      if (typeof window !== 'undefined') {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(updatedItems));
      }
      return { items: updatedItems };
    });
  },
  updateQuantity: (courseId, quantity) => {
    set((state) => {
      const updatedItems = state.items.map((item) =>
        item.courseId === courseId ? { ...item, quantity } : item
      );
      if (typeof window !== 'undefined') {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(updatedItems));
      }
      return { items: updatedItems };
    });
  },
  clearCart: () => {
    set({ items: [] });
    if (typeof window !== 'undefined') {
      localStorage.removeItem(CART_STORAGE_KEY);
    }
  },
  getTotalItems: () => {
    return get().items.reduce((total, item) => total + item.quantity, 0);
  },
  getTotalPrice: () => {
    return get().items.reduce((total, item) => total + item.price * item.quantity, 0);
  },
}));
